import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';
import type { AdditionalMaterialInput, AdditionalMaterialType } from './additionalMaterials';

const BUCKET = 'interview-materials';

export interface FileSource {
  kind: 'file';
  file: File;
}
export interface TextSource {
  kind: 'text';
  text: string;
}
export type Source = FileSource | TextSource;

export interface UploadMaterialsInput {
  endClientId: string;
  resume: FileSource;
  jobDescription: Source;
  notes: string;
  /** Optional typed extras (company info, study material, tips, other). */
  additional?: AdditionalMaterialInput[];
}

interface MaterialRow {
  b2b_company_id: string;
  b2b_end_client_id: string;
  material_type: 'resume' | 'job_description' | AdditionalMaterialType;
  title: string;
  description?: string;
  content?: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  is_active: boolean;
  priority: number;
  created_by?: string;
}

async function uploadFileTo(
  endClientId: string,
  file: File
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() || 'bin';
  // Storage RLS expects the FIRST path segment to be the end_client_id —
  // see the `B2B users can upload interview materials...` policy on storage.objects
  // (split_part(name, '/', 1) → end_client_id).
  const path = `${endClientId}/${crypto.randomUUID()}.${ext}`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (upErr) throw new Error(`storage upload: ${upErr.message}`);
  const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return { url: pub.publicUrl, path };
}

export function useUploadMaterials() {
  const { companyId } = useBranding();
  return useMutation({
    mutationFn: async (input: UploadMaterialsInput): Promise<{ materialIds: string[] }> => {
      const userRes = await supabase.auth.getUser();
      const createdBy = userRes.data.user?.id ?? undefined;

      const rows: MaterialRow[] = [];

      // Resume — always a file in our flow
      const resumeUp = await uploadFileTo(input.endClientId, input.resume.file);
      rows.push({
        b2b_company_id: companyId,
        b2b_end_client_id: input.endClientId,
        material_type: 'resume',
        title: input.resume.file.name,
        file_url: resumeUp.url,
        file_name: input.resume.file.name,
        file_type: input.resume.file.type,
        file_size: input.resume.file.size,
        is_active: true,
        priority: 1,
        ...(createdBy ? { created_by: createdBy } : {}),
      });

      // JD — file or pasted text
      if (input.jobDescription.kind === 'file') {
        const jdUp = await uploadFileTo(input.endClientId, input.jobDescription.file);
        rows.push({
          b2b_company_id: companyId,
          b2b_end_client_id: input.endClientId,
          material_type: 'job_description',
          title: input.jobDescription.file.name,
          file_url: jdUp.url,
          file_name: input.jobDescription.file.name,
          file_type: input.jobDescription.file.type,
          file_size: input.jobDescription.file.size,
          is_active: true,
          priority: 2,
          ...(createdBy ? { created_by: createdBy } : {}),
        });
      } else {
        rows.push({
          b2b_company_id: companyId,
          b2b_end_client_id: input.endClientId,
          material_type: 'job_description',
          title: 'Job description',
          content: input.jobDescription.text,
          is_active: true,
          priority: 2,
          ...(createdBy ? { created_by: createdBy } : {}),
        });
      }

      // Notes — optional custom row
      const noteText = input.notes.trim();
      if (noteText) {
        rows.push({
          b2b_company_id: companyId,
          b2b_end_client_id: input.endClientId,
          material_type: 'custom',
          title: 'Recruiter notes',
          content: noteText,
          is_active: true,
          priority: 3,
          ...(createdBy ? { created_by: createdBy } : {}),
        });
      }

      // Additional materials — ordered after resume (1), JD (2) and notes (3)
      let priority = 4;
      for (const item of input.additional ?? []) {
        if (item.source.kind === 'file') {
          const up = await uploadFileTo(input.endClientId, item.source.file);
          rows.push({
            b2b_company_id: companyId,
            b2b_end_client_id: input.endClientId,
            material_type: item.type,
            title: item.title,
            file_url: up.url,
            file_name: item.source.file.name,
            file_type: item.source.file.type,
            file_size: item.source.file.size,
            is_active: true,
            priority: priority++,
            ...(createdBy ? { created_by: createdBy } : {}),
          });
        } else {
          const text = item.source.text.trim();
          if (!text) continue;
          rows.push({
            b2b_company_id: companyId,
            b2b_end_client_id: input.endClientId,
            material_type: item.type,
            title: item.title,
            content: text,
            is_active: true,
            priority: priority++,
            ...(createdBy ? { created_by: createdBy } : {}),
          });
        }
      }

      const { data: inserted, error: insertErr } = await supabase
        .schema('neurax' as never)
        .from('end_client_materials' as never)
        .insert(rows as never)
        .select();

      if (insertErr) throw new Error(`end_client_materials insert: ${insertErr.message}`);

      const insertedRows = (inserted ?? []) as { id: string; file_url?: string | null }[];

      // Fire-and-forget: trigger AI parsing for every uploaded file (resume, JD and extras).
      // generate-interview-prompt polls for parsed content downstream, so we don't block here.
      // Errors are swallowed — admin shouldn't see Gemini OCR hiccups.
      for (const row of insertedRows) {
        if (!row.file_url) continue;
        supabase.functions
          .invoke('parse-material-file', { body: { material_id: row.id } })
          .catch((err) => {
            console.warn(`parse-material-file failed for ${row.id}:`, err);
          });
      }

      return { materialIds: insertedRows.map((r) => r.id) };
    },
  });
}
